import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';

import { bankingSchema, type BankingValues } from '@/lib/validators/onboarding';
import { useOnboardingStore } from '@/lib/stores/onboarding';
import { formatIban } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Field } from '@/components/ui/field';
import { Card, CardContent } from '@/components/ui/card';

export const Route = createFileRoute('/onboarding/banking')({
  component: BankingStep,
});

const PAYMENT_TERMS_OPTIONS = [
  { value: '8',  label: '8 dni' },
  { value: '15', label: '15 dni' },
  { value: '30', label: '30 dni (priporočeno)' },
  { value: '45', label: '45 dni' },
  { value: '60', label: '60 dni' },
];

function BankingStep() {
  const { t } = useTranslation('onboarding');
  const navigate = useNavigate();
  const { banking, setBanking, setStep } = useOnboardingStore();

  const {
    register,
    control,
    handleSubmit,
    setValue,
    formState: { errors, isValid },
  } = useForm<BankingValues>({
    resolver: zodResolver(bankingSchema),
    defaultValues: {
      iban:              banking.iban ?? '',
      paymentTermsDays:  banking.paymentTermsDays ?? 30,
      lateInterestPct:   banking.lateInterestPct ?? 8,
    },
    mode: 'onChange',
  });

  function onIbanChange(e: React.ChangeEvent<HTMLInputElement>) {
    const formatted = formatIban(e.target.value);
    setValue('iban', formatted, { shouldValidate: true });
  }

  function onSubmit(data: BankingValues) {
    setBanking(data);
    setStep('services');
    void navigate({ to: '/onboarding/services' });
  }

  function goBack() {
    setStep('identity');
    void navigate({ to: '/onboarding/identity' });
  }

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="mb-6">
          <h2 className="font-display text-display-sm text-forge">{t('banking.heading')}</h2>
          <p className="text-sm text-steel mt-1">{t('banking.subheading')}</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
          <Field
            id="iban"
            label={t('banking.fields.iban')}
            hint={t('banking.fields.iban_hint')}
            error={errors.iban?.message}
            required
          >
            <Controller
              name="iban"
              control={control}
              render={({ field }) => (
                <Input
                  id="iban"
                  inputMode="text"
                  placeholder="SI56 0123 4567 8901 234"
                  error={errors.iban?.message}
                  value={field.value}
                  onChange={onIbanChange}
                  className="font-mono tracking-wider"
                />
              )}
            />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field
              id="paymentTermsDays"
              label={t('banking.fields.payment_terms_days')}
              error={errors.paymentTermsDays?.message}
              required
            >
              <Controller
                name="paymentTermsDays"
                control={control}
                render={({ field }) => (
                  <Select
                    id="paymentTermsDays"
                    options={PAYMENT_TERMS_OPTIONS}
                    error={errors.paymentTermsDays?.message}
                    value={String(field.value)}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                  />
                )}
              />
            </Field>

            <Field
              id="lateInterestPct"
              label={t('banking.fields.late_interest_pct')}
              error={errors.lateInterestPct?.message}
              required
            >
              <Input
                id="lateInterestPct"
                type="number"
                step="0.1"
                min="0"
                max="30"
                error={errors.lateInterestPct?.message}
                {...register('lateInterestPct')}
              />
            </Field>
          </div>

          <div className="flex justify-between pt-2">
            <Button type="button" variant="ghost" onClick={goBack}>
              {t('actions.back', { ns: 'common' })}
            </Button>
            <Button type="submit" variant="primary" size="lg" disabled={!isValid}>
              {t('actions.next', { ns: 'common' })}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
